import { useState } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { Text } from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

// ── Section label ──

interface SectionLabelProps {
  children: React.ReactNode;
  className?: string;
}

export function SectionLabel({ children, className }: SectionLabelProps) {
  return (
    <p className={cn("text-xs text-muted-foreground", className)}>{children}</p>
  );
}

// ── Bordered card ──

interface InfoCardProps {
  children: React.ReactNode;
  className?: string;
}

export function InfoCard({ children, className }: InfoCardProps) {
  return (
    <div className={cn("max-w-sm rounded-md border border-border", className)}>
      {children}
    </div>
  );
}

// ── Row inside an InfoCard ──

interface InfoRowProps {
  children: React.ReactNode;
  className?: string;
  border?: boolean;
}

export function InfoRow({ children, className, border = false }: InfoRowProps) {
  return (
    <div
      className={cn(
        "px-4 py-3 text-sm text-center",
        border && "border-t border-border",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── Checkout-style status page ──

interface StatusOverlayProps {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  cancelLabel: string;
  onCancel: () => void | Promise<void>;
  children?: React.ReactNode;
}

export function StatusOverlay({
  icon,
  title,
  description,
  cancelLabel,
  onCancel,
  children,
}: StatusOverlayProps) {
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await onCancel();
    } finally {
      setCancelling(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 overflow-y-auto rounded-md border border-border bg-background"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: "easeOut" }}
        className="mx-auto flex h-full w-full max-w-lg flex-col items-center justify-center"
      >
        {/* Loading icon */}
        <div className="my-10">{icon}</div>

        {/* Heading + description */}
        <div className="flex flex-col items-center text-center">
          <Text variant="h3" className="mb-2">
            {title}
          </Text>
          <Text variant="muted" className="max-w-md text-base">
            {description}
          </Text>
        </div>

        {/* Content slot */}
        {children && <div className="mt-5">{children}</div>}

        {/* Cancel */}
        <div className="mt-6 mx-auto">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? <Spinner className="size-4" /> : cancelLabel}
          </Button>
        </div>

        {/* Footer */}
        <p className="mt-40 text-center text-xs text-muted-foreground">
          Secured by{" "}
          <a
            href="https://snippe.sh"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold hover:underline"
          >
            Snippe
          </a>
        </p>
      </motion.div>
    </motion.div>
  );
}
